import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { first, Observable } from 'rxjs';

import { ApiService } from '@core/services/api.service';
import { cleanHTTPParams } from '@core/utils/helpers';
import { PaginatedResponse } from '@core/models/response';
import {
  InventoryBatchDto,
  InventoryExceptionsDto,
  InventoryItemDto,
  InventoryItemSiteDto,
  InventoryListFilters,
  InventoryMetricsDto,
  UpdateInventoryBatchPayload,
} from '@core/models/inventory.model';
import { CommentLoaderKey } from '@core/enums/comment.enum';
import { LoadingService } from './loading';
import { SnackbarService } from './snackbar.service';
import { CommentableResourceService } from '@core/interfaces/commentable-resource.interface';
import { Comment } from '@core/models/comment.model';
import { Attachment } from '@core/models';

const endpoint = 'v1/inventory';

@Injectable({
  providedIn: 'root',
})
export class InventoryService implements CommentableResourceService {
  private readonly api = inject(ApiService);
  loader = inject(LoadingService);
  snack = inject(SnackbarService);

  list(filters: InventoryListFilters): Observable<PaginatedResponse<InventoryItemDto>> {
    const { flags, ...rest } = filters;
    let params = new HttpParams({ fromObject: cleanHTTPParams(rest as Record<string, unknown>) });

    if (Array.isArray(flags) && flags.length) {
      params = params.set('flags', JSON.stringify(flags));
    }

    return this.api.get<PaginatedResponse<InventoryItemDto>>(endpoint, { params }).pipe(first());
  }

  listBatches(inventoryItemId: string): Observable<InventoryBatchDto[]> {
    return this.api.get<InventoryBatchDto[]>(`${endpoint}/${inventoryItemId}/batches`).pipe(first());
  }

  listSites(): Observable<InventoryItemSiteDto[]> {
    return this.api.get<InventoryItemSiteDto[]>(`${endpoint}/sites`).pipe(first());
  }

  updateBatch(batchId: string, payload: UpdateInventoryBatchPayload): Observable<InventoryBatchDto> {
    return this.api.put<InventoryBatchDto, UpdateInventoryBatchPayload>(endpoint, batchId, payload).pipe(first());
  }

  metrics(): Observable<InventoryMetricsDto> {
    return this.api.get<InventoryMetricsDto>(`${endpoint}/metrics`).pipe(first());
  }

  exceptions(): Observable<InventoryExceptionsDto> {
    return this.api.get<InventoryExceptionsDto>(`${endpoint}/exceptions`).pipe(first());
  }

  listComments(resourceId: string): Observable<Comment[]> {
    return this.api
      .get(`${endpoint}/${resourceId}/comments`)
      .pipe(first(), this.loader.set(CommentLoaderKey.LIST_COMMENTS), this.snack.operator());
  }

  createComment(resourceId: string, formData: FormData): Observable<Comment> {
    return this.api
      .post(`${endpoint}/${resourceId}/comments`, formData)
      .pipe(first(), this.loader.set(CommentLoaderKey.CREATE_COMMENT), this.snack.operator());
  }

  listAttachments(resourceId: string): Observable<Attachment[]> {
    return this.api
      .get(`${endpoint}/${resourceId}/attachments`)
      .pipe(first(), this.loader.set(CommentLoaderKey.LIST_COMMENTS), this.snack.operator());
  }

  createAttachment(resourceId: string, file: File): Observable<Attachment> {
    const formData: FormData = new FormData();
    formData.set('file', file);
    return this.api
      .post(`${endpoint}/${resourceId}/attachments`, formData)
      .pipe(first(), this.loader.set(CommentLoaderKey.CREATE_COMMENT), this.snack.operator());
  }
}
